import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { EnvState } from 'app/modules/shared/helpers/env-state';
import { AuthRepository, getStoredToken } from 'app/state/auth.repository';
import { MessageText, ToastService } from 'app/helpers/services/toastService';
import { AuthService } from 'app/state/auth.service';
import { TenantFeatures } from 'app/state/feature.repository';
import { UiRepository } from 'app/state/ui.repository';
import { DocumentTypesRepository } from '../../../../state/documenttype.repository';
import { DocumentTypesService } from '../../../../state/documenttype.service';
import { TenantsRepository } from 'app/state/tenants.repository';
import { DataService } from 'app/shared/data.service';
import { UserGroupsRepository } from 'app/state/usergroup.repository';
import { ClearCacheService } from 'app/helpers/clearCacheValuesService';
import { OnboardingService } from 'app/api/services/onboarding.service';
import { NotificationsRepository } from 'app/state/notifications.repository';
import { NotificationsService } from 'app/state/notifications.service';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { DatalistOption } from 'app/state/abstract/base.repository';
import { combineLatest } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidebarState } from 'app/api/services/sidebarState.service';
import { TenantBrandingService } from 'app/state/tenant-branding.service';
import { compareTenantOptions } from 'app/helpers/arrayToDataListOptonConverter';

@UntilDestroy()
@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})
export class SidebarComponent implements OnInit {
  TenantFeatures = TenantFeatures;
  features = this.env.features;

  curLang: string = 'en';
  langSub: any;
  uploadSubscribe: any;
  isUploading: boolean = false;

  IsTenantsLoad: boolean = false;
  IsDocTypesLoad: boolean = false;
  isCoach: boolean = false;
  isManager: boolean = false;

  // Pinned tenant switcher (above the user card). Same wiring as the
  // version inside <app-sidebar-items>, but rendered outside the scrollable
  // nav so it stays visible regardless of how long the item list is.
  tenantsOptions: Array<DatalistOption> = [];
  tenantId: string | null = null;
  activeTenantLabel: string = '';

  // Custom brand logo set on the active tenant's "Information" tab. When
  // present (and the tenant isn't Nybolig) it replaces the default IGNTR mark.
  customLogoUrl: string | null = null;

  private readonly NYBOLIG_TENANT_ID = '8fa1d45f-a881-40e5-9477-08dc7d94ec51';

  get isNybolig(): boolean {
    return this.tenantId?.toLowerCase() === this.NYBOLIG_TENANT_ID;
  }

  /** First letter of the active tenant — used by the avatar tile that
   * overlays the left side of the tenant select chip. */
  get activeTenantInitial(): string {
    return (this.activeTenantLabel || '?').charAt(0).toUpperCase();
  }

  /**
   * Deterministic gradient for the tenant avatar — same approach as the
   * tenant picker page, so the same tenant has the same color everywhere.
   */
  get activeTenantAvatarBg(): string {
    const label = this.activeTenantLabel || '';
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = (hash * 31 + label.charCodeAt(i)) & 0xffffffff;
    }
    const hue = Math.abs(hash) % 360;
    return `linear-gradient(135deg, oklch(0.62 0.15 ${hue}), oklch(0.48 0.18 ${hue}))`;
  }

  constructor(
    private auth: AuthService,
    public repo: AuthRepository,
    public tenantRepo: TenantsRepository,
    public ui: UiRepository,
    private router: Router,
    public env: EnvState,
    public doctypesRepo: DocumentTypesRepository,
    public docttpeService: DocumentTypesService,
    private dataService: DataService,
    public userGroupRepo: UserGroupsRepository,
    private clearCacheService: ClearCacheService,
    private obService: OnboardingService,
    private notificationsRepo: NotificationsRepository,
    private notificationsService: NotificationsService,
    public generalDataRepo: GeneralDataRepository,
    private storage: LocalStorageService,
    // public so the search input in the template can `[(ngModel)]` against
    // sidebarState.searchQuery
    public sidebarState: SidebarState,
    private branding: TenantBrandingService,
    private toastService: ToastService,
  ) {
    const service_worker_chache_was_cleaned = localStorage.getItem(
      'service_worker_chache_was_cleaned'
    );
    if (!service_worker_chache_was_cleaned) {
      clearCacheService.clearServiceWorkerCache();
    }

    this.uploadSubscribe = this.dataService.isUploading$.subscribe((x: any) => {
      this.isUploading = x;
    });
    this.langSub = this.dataService.curUserLang.subscribe(
      (x) => (this.curLang = x)
    );
  }

  ngOnInit(): void {
    combineLatest([
      this.generalDataRepo.tenantsOptions$,
      this.storage.getTenantId$(),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([options, id]) => {
        this.tenantsOptions = [...options].sort(compareTenantOptions);
        this.tenantId = id;
        const match = this.tenantsOptions.find((o) => o.value === id);
        this.activeTenantLabel = match?.label ?? '';
      });

    this.branding.homeLogoUrl$
      .pipe(untilDestroyed(this))
      .subscribe((url) => (this.customLogoUrl = url));
  }

  setActiveTenant(tenantId?: any): void {
    // Mirrors the legacy implementation in SidebarItemsComponent so the chip
    // behaves identically whether rendered here or there.
    this.sidebarState.isMainOpen = false;
    this.sidebarState.isCrmOpen = false;
    this.sidebarState.isIgniteOpen = false;
    this.sidebarState.isAdminOpen = false;
    this.sidebarState.isCrmAdminOpen = false;
    const token = getStoredToken();
    if (!token) {
      console.error('Cannot switch tenant: no auth token available');
      return;
    }
    // Switch the tenant server-side FIRST. Only mirror the change into client
    // state (localStorage/chip) once the server confirms — otherwise a failed
    // request leaves the UI on the new tenant while the backend still scopes
    // every query to the old one, showing the previous tenant's data.
    this.auth
      .setActiveTenant(token, tenantId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.tenantId = tenantId;
          if (tenantId) {
            localStorage.setItem('activeTenantId', tenantId);
          } else {
            localStorage.removeItem('activeTenantId');
          }
          this.storage.setTenantId(tenantId);
          this.generalDataRepo.updateAllGeneralData(true);
          this.router.navigate(['/home']);
        },
        error: (err) => {
          // Leave the client on the previous tenant; nothing was changed yet.
          console.error('Failed to switch tenant', err);
          this.toastService.showError(MessageText.switchTenantFailed);
        },
      });
  }

  menuclose() {
    this.ui.toggleMobileDrawer(false);
  }

  logout() {
    this.menuclose();
    this.auth.logout();

    return false;
  }

  resetLoading() {
    this.IsDocTypesLoad = false;
  }

  unimpersonate() {
    this.menuclose();
    this.auth.unimpersonate().subscribe({
      complete: () => {
        this.router.navigate(['/']);
        return false;
      },
    });
  }

  ngOnDestroy() {
    this.uploadSubscribe.unsubscribe();
    this.langSub.unsubscribe();
  }

  showPage(pageName: string) {
    const tenId = localStorage.getItem('activeTenantId');
    let admin = this.repo.isAnyAdmin$;
    switch (pageName) {
      case 'reports': {
        if (tenId && admin) {
          return true;
        }
        return false;
      }
      default: {
        return false;
        break;
      }
    }
  }
}
